import React, { useEffect, useState, useCallback, useMemo } from "react";
import "../styles/SATRequest.css";
import "../styles/AdminApplications.css";
import { apiClient } from "../services/apiClient";
import { useConfirm } from "../providers/ConfirmProvider";
import { useI18n } from "../providers/I18nProvider";
import { FilterBar, SearchInput, EmptyState } from "../components/ListToolbar";

// Başvuru etiketleri yönetimi.
// İnceleyenler başvurulara etiket ekler (AdminApplicationsPage), bu sayfa
// etiket havuzunu yönetmek için (yeniden adlandırma, renk değiştirme, silme).
//
// Erişim: admin/it (server-side gateli; routes/applicationTagRoutes.js).

const emptyForm = { name: "", color: "#1677ff" };

const AdminApplicationTagsPage = () => {
  const confirm = useConfirm();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("name");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get("/application-tags");
      setRows(res.data || []);
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("applications.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    const list = rows.filter((r) => !q || String(r.name || "").toLocaleLowerCase("tr-TR").includes(q));
    const sorters = {
      name: (a, b) => String(a.name || "").localeCompare(String(b.name || ""), "tr"),
      newest: (a, b) => new Date(b.created_at) - new Date(a.created_at),
      oldest: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    };
    return [...list].sort(sorters[sort] || sorters.name);
  }, [rows, search, sort]);

  const startEdit = (row) => {
    window.scrollTo?.({ top: 0, behavior: "smooth" });
    setEditingId(row.id);
    setForm({ name: row.name || "", color: row.color || "#1677ff" });
  };

  const cancel = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const save = async () => {
    if (!form.name.trim()) {
      setError(t("applications.tags.nameRequired"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (editingId) {
        await apiClient.put(`/application-tags/${editingId}`, form);
      } else {
        await apiClient.post("/application-tags", form);
      }
      cancel();
      await load();
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("applications.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (
      !(await confirm(t("applications.tags.deleteConfirm", { name: row.name })))
    )
      return;
    setBusy(true);
    setError("");
    try {
      await apiClient.delete(`/application-tags/${row.id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("applications.deleteFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sat-page apps-meta-page">
      <div className="sat-page-header">
        <div>
          <h2>{t("applications.tags.title")}</h2>
          <div className="subtitle">{t("applications.tags.subtitle")}</div>
        </div>
      </div>

      {error && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}

      <form
        className="apps-form-card"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <h3>{editingId ? t("applications.editTitle", { id: editingId }) : t("applications.tags.addTitle")}</h3>
        <div className="apps-form-grid apps-form-grid--tags">
          <label>
            {t("applications.tags.name")}
            <input
              type="text"
              className="apps-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label>
            {t("applications.tags.color")}
            <input
              type="color"
              className="apps-input apps-color"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              aria-label={t("applications.tags.colorAria")}
            />
          </label>
          <div className="apps-form-actions">
            <button type="submit" disabled={busy} className="sat-btn sat-btn--primary">
              {editingId ? t("applications.update") : t("applications.add")}
            </button>
            {editingId && (
              <button type="button" onClick={cancel} className="sat-btn">
                {t("applications.cancel")}
              </button>
            )}
          </div>
        </div>
      </form>

      <FilterBar showReset={Boolean(search)} onReset={() => setSearch("")}>
        <SearchInput value={search} onChange={setSearch} placeholder={t("applications.tags.searchPlaceholder")} />
        <select
          className="leaves-filter-input lt-input"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          aria-label={t("applications.filters.sort")}
        >
          <option value="name">{t("applications.tags.sortName")}</option>
          <option value="newest">{t("applications.tags.sortNewest")}</option>
          <option value="oldest">{t("applications.tags.sortOldest")}</option>
        </select>
        <span className="apps-count">{visibleRows.length} / {rows.length}</span>
      </FilterBar>

      {loading && <div className="sat-empty">{t("applications.loading")}</div>}
      {!loading && visibleRows.length === 0 && (
        <EmptyState message={rows.length === 0 ? t("applications.empty") : t("applications.tags.noMatch")} />
      )}
      {!loading && visibleRows.length > 0 && (
        <table className="sat-table apps-meta-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t("applications.tags.colTag")}</th>
              <th>{t("applications.tags.color")}</th>
              <th>{t("applications.tags.colCreated")}</th>
              <th>{t("applications.columns.action")}</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r) => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td>
                  <span className="apps-tag" style={{ background: r.color || "#1677ff" }}>
                    {r.name}
                  </span>
                </td>
                <td>
                  <code>{r.color}</code>
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {new Date(r.created_at).toLocaleDateString("tr-TR")}
                </td>
                <td>
                  <div className="apps-row-actions">
                    <button type="button" onClick={() => startEdit(r)} className="sat-btn">
                      {t("applications.edit")}
                    </button>
                    <button type="button" onClick={() => remove(r)} disabled={busy} className="sat-btn sat-btn--danger">
                      {t("applications.delete")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminApplicationTagsPage;
